/**
 * Оперативная раскладка денег.
 *
 * Месячный план отвечает на вопрос «к чему я иду»: он считается от ожидаемого
 * дохода и живёт календарём. Человеку же каждый день нужен другой ответ —
 * «сколько я могу потратить из того, что есть». Пока виджеты считались из
 * месячного плана, они обещали деньги, которых нет: у того, кто завёл
 * приложение в середине месяца, и у любого, чей доход не по календарю.
 *
 * Раскладка — это **принятая версия** ответа. Не пересчёт на каждый чих:
 * если делить остаток заново после каждой траты, покупка продуктов уменьшит
 * и «Свободные траты», и человек прочтёт это как ошибку. Поэтому доли фиксируются в
 * момент согласия, а тратится потом из своей корзины.
 *
 * Главный инвариант, ради которого всё написано:
 *
 * ```
 * ликвидные деньги
 *   = связанные резервы (платежи и цели)
 *   + остаток «Повседневные нужды»
 *   + остаток «Свободные траты»
 *   + перенос через ближайший приход
 *   + не распределено
 * ```
 *
 * Резервы при этом остаются источником правды о закреплённых деньгах:
 * второй изменяемый остаток внутри раскладки означал бы две правды об одних
 * и тех же рублях.
 */
import { ZERO, add, clampPos, formatMoney, minM, neg, share, sub, type Money } from './money.js';
import { diffDays, isAfter, isBefore, type PlainDate } from './date.js';
import { isKnown, type Amount } from './amount.js';
import { conservativeIncome } from './available.js';
import {
  confirmedDate, incomeOccurrences, nextForStream, rhythmOf, roleOfStream,
  type Horizon,
} from './income.js';
import { cardPayments, notCovered, type ForecastEvent } from './forecast.js';
import { paymentInstances, unsettledInstances, type PaymentInstance } from './instances.js';
import { selfShare } from './mode.js';
import type { PlanMode } from './plan.js';
import type {
  Account, CreditTerms, Goal, IncomeStream, Obligation, Reserve, ReserveTarget,
} from './types.js';

/** Почему раскладка пересматривается. Видно человеку и лежит в истории. */
export type AllocationBasis =
  | 'onboarding'
  | 'income'
  | 'manual'
  | 'obligation_change'
  | 'goal_change'
  | 'reconcile'
  /** Обнаружена просрочка: деньги оказались связаны сильнее, чем думали. */
  | 'overdue_detected';

/** Деньги, закреплённые под конкретное наступление платежа. */
export interface AllocationObligationLine {
  instanceId: string;
  obligationId: string;
  label: string;
  dueDate: PlainDate;
  /** Сколько закрепить сверх уже закреплённого. */
  amount: Money;
}

/**
 * Деньги, закреплённые под платёж по кредитке.
 *
 * Отдельно от обязательств: у карты нет наступления платежа, а конверт
 * заводится под счёт карты (`forKind: 'credit'`). Сумма — из прогноза:
 * остаток выписки либо покупки после неё, если срок до прихода.
 */
export interface AllocationCardLine {
  accountId: string;
  label: string;
  dueDate: PlainDate;
  /** Сколько закрепить сверх уже закреплённого. */
  amount: Money;
}

/** Деньги, закреплённые под конкретную цель. */
export interface AllocationGoalLine {
  goalId: string;
  name: string;
  amount: Money;
}

/**
 * Строки раскладки.
 *
 * Агрегатов вида `Record<PlanLineKind, Money>` здесь недостаточно: применение
 * обязано знать, **какое наступление платежа** и **какая цель** получили
 * деньги, иначе конверт некуда записать.
 */
export interface AllocationLines {
  obligations: AllocationObligationLine[];
  /**
   * Платежи по кредиткам до горизонта.
   *
   * Необязательны: у версий, принятых до них, поля нет, и читаются они как
   * «карт до прихода не было». Раньше раскладка карт не знала вовсе, и
   * платёж по выписке раньше прихода не держал ни рубля — деньги на него
   * уходили в корзины.
   */
  cards?: AllocationCardLine[];
  /** Обычная жизнь до горизонта. */
  need: Money;
  /** Свободные траты до горизонта. */
  want: Money;
  goals: AllocationGoalLine[];
  /**
   * Перенос через ближайший приход.
   *
   * Деньги, которые нельзя раздать сегодня, потому что ближайший приход
   * меньше расходов следующего отрезка. Не резерв и не трата — запрет
   * распределять.
   */
  carry: Money;
  /** Не распределено: деньги есть, решения по ним нет. */
  unallocated: Money;
  /**
   * Покупки по кредитке прошлой версии, ещё не погашенные: «Распределить»
   * создаёт новую версию, и без этой строки погашение списалось бы со
   * свободных денег второй раз.
   */
  owed?: { accountId: string; amount: Money }[];
}

export interface AcceptedAllocation {
  id: string;
  scope: 'allocation';
  /** Когда человек согласился. */
  acceptedAt: string;
  /** С какого момента раскладка действует: по ней считаются траты. */
  effectiveFrom: string;
  basis: AllocationBasis;
  /** Снимок горизонта на момент принятия — вместе с объяснением. */
  horizon: Horizon;
  /** Ликвидные деньги на момент принятия. */
  liquid: Money;
  /** Связанные резервы на момент принятия. */
  reserved: Money;
  lines: AllocationLines;
  /** Предыдущая версия. Цепочка решений, а не перезапись. */
  supersedes?: string;
  /** Отпечаток данных, на которых построено предложение. */
  inputVersion: string;
  /**
   * Отпечаток решений: по нему раскладка «устаревает».
   *
   * Отдельно от `inputVersion` — см. `allocationStructureVersion`. У версий,
   * принятых до его появления, поля нет, и устаревшими они не считаются:
   * сравнивать их было бы не с чем, а прежний отпечаток давал «устарела»
   * всегда.
   */
  structureVersion?: string;
  /**
   * Что человек решил оставить необеспеченным при нехватке.
   *
   * **Устарело.** Поле осталось у версий, созданных до экрана выбора: тогда
   * список заполнялся автоматически, то есть приложение само решало, какой
   * платёж оставить без денег. Такие версии полноценным подтверждением не
   * считаются (`needsReview`), но переписывать их историю нельзя — это
   * решения, принятые в своё время, пусть и неверным способом.
   */
  unfunded?: { goalIds: string[]; note?: string };
  /**
   * Решение человека при нехватке — построчно и с временем.
   *
   * Без него версия с нехваткой не принимается: выбирать, какой обязательный
   * платёж останется без денег, приложение не вправе. Хранится, чтобы
   * следующий пересчёт не пересмотрел решение молча.
   */
  shortfall?: ShortfallDecision;
}

/** Одна строка конфликта: чего требует жизнь или платёж и что с ним делать. */
export interface ShortfallLine {
  /** `obligation` — наступление платежа, `card` — платёж по кредитке, `need` — обычная жизнь. */
  kind: 'obligation' | 'card' | 'need';
  /** Ключ строки: идентификатор наступления либо `need`. */
  id: string;
  label: string;
  dueDate?: PlainDate;
  /** Сколько требуется всего. */
  required: Money;
  /** Сколько уже обеспечено конвертами. */
  secured: Money;
  /** Что предлагает приложение. Рекомендация, а не действие. */
  proposed: Money;
  /** Чем обернётся нехватка именно здесь. */
  consequence: string;
}

export interface ShortfallDecision {
  decidedAt: string;
  /** Сколько человек решил выделить каждой строке. */
  lines: { id: string; fund: Money }[];
  /** Сколько остаётся необеспеченным по его решению. */
  left: Money;
}

/**
 * Версия принята без человека.
 *
 * Такое бывало до появления экрана выбора: нехватка распределялась
 * автоматически. Считать это подтверждением нельзя — приложение решало за
 * человека, — поэтому версия помечается и на главной просит пересмотра.
 */
export const needsReview = (a: AcceptedAllocation): boolean =>
  a.unfunded !== undefined && a.shortfall === undefined;

// ─────────────────────────────────────────────────────────────── предложение

export interface AllocationPlanInput {
  today: PlainDate;
  accounts: readonly Account[];
  reserves: readonly Reserve[];
  obligations: readonly Obligation[];
  /**
   * Условия карт. Необязательны только ради старых вызовов: без них платежи
   * по картам в раскладке не видны, как было прежде.
   */
  creditTerms?: readonly CreditTerms[];
  goals: readonly Goal[];
  incomeStreams: readonly IncomeStream[];
  /** Обычная жизнь в месяц. «Не знаю» меняет весь ответ. */
  needBaseline: Amount;
  horizon: Horizon;
  mode: PlanMode;
  /** Сколько взносов в цели уже сделано в этом месяце. */
  fundedThisMonth?: Record<string, Money>;
  /** Явно выбранный человеком период защиты, когда горизонта нет. */
  protectDays?: number;
  /** С какого дня приложение ведёт учёт: граница автоматической просрочки. */
  trackingStartedAt?: PlainDate;
  /** Действующая раскладка: от неё считается «было → стало». */
  current?: AcceptedAllocation | null;
}

export interface AllocationProposal {
  lines: AllocationLines;
  /** Ликвидные деньги, от которых считали. */
  liquid: Money;
  /** Уже связанные резервы. */
  reserved: Money;
  /** Сколько не хватает на защищённый слой. */
  shortfall: Money;
  /** Цели, взнос которых под угрозой из-за нехватки. */
  atRisk: AllocationGoalLine[];
  /** Чего расчёт не знает. */
  unknowns: string[];
  /** Отпечаток входных данных. */
  inputVersion: string;
  /** Отпечаток решений — для вопроса «устарела ли принятая раскладка». */
  structureVersion: string;
}

/** Ликвидные деньги: всё, чем можно расплатиться сегодня. */
export const liquidOf = (accounts: readonly Account[]): Money => add(
  ...accounts
    .filter((a) => a.isLiquid && !a.archived && a.kind !== 'credit_card')
    .map((a) => a.balance),
);

/** Связанные деньги: конверты на ликвидных счетах. */
export function reservedOf(
  accounts: readonly Account[], reserves: readonly Reserve[],
): Money {
  const liquidIds = new Set(
    accounts.filter((a) => a.isLiquid && !a.archived && a.kind !== 'credit_card').map((a) => a.id),
  );
  return add(...reserves.filter((r) => liquidIds.has(r.accountId)).map((r) => r.amount));
}

/** Сколько уже закреплено под это требование, где бы конверт ни лежал. */
const coveredFor = (reserves: readonly Reserve[], kind: Reserve['forKind'], id: string): Money =>
  add(...reserves.filter((r) => r.forKind === kind && r.forId === id).map((r) => r.amount));

/**
 * Перенос через ближайший приход.
 *
 * Аванс бывает меньше, чем нужно прожить до зарплаты. Если раздать сегодняшние
 * деньги целям и «Свободные траты», нехватка обнаружится только после аванса — когда
 * тратить будет уже нечего.
 *
 * Поэтому считается следующий отрезок: от ближайшего прихода до следующего за
 * ним. Сколько там нужно повседневные нужды и платежи, минус осторожная оценка самого
 * прихода, — столько сегодняшних денег распределять нельзя.
 *
 * Будущие деньги при этом сегодняшними **не становятся**: расчёт только
 * ограничивает раздачу.
 *
 * Если после ближайшего прихода следующая дата неизвестна, перенос через
 * него не выдумывается, и приложение планирует только **до** ближайшего
 * прихода. Это допустимо ровно по одной причине: когда приход случится,
 * человек примет новую раскладку — уже на настоящих деньгах. А если приход
 * не состоится, горизонт перейдёт в `awaiting`, и ни цели, ни свободные
 * деньги не будут пересчитаны оптимистично: продукт не станет делать вид,
 * что зарплата пришла.
 */
export function carryThroughIncome(input: {
  today: PlainDate;
  incomeStreams: readonly IncomeStream[];
  obligations: readonly Obligation[];
  reserves: readonly Reserve[];
  needBaseline: Amount;
  horizon: Horizon;
  /** Счета и условия карт: платёж по выписке в следующем отрезке тоже его ест. */
  accounts?: readonly Account[];
  creditTerms?: readonly CreditTerms[];
}): Money {
  const first = confirmedDate(input.horizon);
  if (!first || !isKnown(input.needBaseline)) return ZERO;

  // Ближайший приход: сколько денег он принесёт и что будет после него.
  const arriving = add(...input.incomeStreams
    .filter((s) => roleOfStream(s) === 'checkpoint')
    .filter((s) => incomeOccurrences(s, first, first).length > 0)
    .map((s) => conservativeIncome(s)));
  if (arriving <= ZERO) return ZERO;

  const nextAfter = input.incomeStreams
    .map((s) => nextForStream(s, first))
    .filter((d): d is PlainDate => d !== null)
    .sort()[0];
  if (!nextAfter) return ZERO;

  const days = Math.max(1, diffDays(first, nextAfter));
  const living = share(input.needBaseline.value, days / 30);
  const payments = add(...paymentInstances({
    obligations: [...input.obligations],
    reserves: [...input.reserves],
    today: input.today,
    from: first,
    to: nextAfter,
  }).filter((i) => !i.settled).map((i) => i.remaining));
  // Платёж по карте в следующем отрезке — из того же прогноза, что и на
  // главной: иначе аванс «хватал» бы на отрезок, в котором карта съедает его
  // целиком.
  const cards = add(...cardsDue(input, first, nextAfter).map(notCovered));

  return clampPos(sub(add(living, payments, cards), arriving));
}

/**
 * Платежи по картам в отрезке `[from, to]` — из общего прогноза.
 *
 * Конверты передаются как записаны: раскладка и в обязательствах считает от
 * записанных, и два правила в одном расчёте развели бы защищённый слой.
 */
function cardsDue(
  input: {
    today: PlainDate; accounts?: readonly Account[]; creditTerms?: readonly CreditTerms[];
    reserves: readonly Reserve[];
  },
  from: PlainDate,
  to: PlainDate,
): ForecastEvent[] {
  if (!input.accounts || !input.creditTerms || input.creditTerms.length === 0) return [];
  return cardPayments({
    today: input.today, accounts: input.accounts, creditTerms: input.creditTerms,
    reserves: input.reserves,
  }).filter((e) => !isBefore(e.date, from) && !isAfter(e.date, to));
}

/**
 * Предложение раскладки.
 *
 * Порядок слоёв неслучаен и повторяет тот, что принят в §4 разбора:
 * защищённое (платежи и минимумы) → необходимое (жизнь) → перенос →
 * остаток делится между целями и «Свободные траты» по выбранному режиму.
 */
export function proposeAllocation(input: AllocationPlanInput): AllocationProposal {
  const unknowns: string[] = [];
  const liquid = liquidOf(input.accounts);
  const reserved = reservedOf(input.accounts, input.reserves);
  // Свободные деньги: всё, что не закреплено конвертами.
  let rest = clampPos(sub(liquid, reserved));

  const horizonDate = confirmedDate(input.horizon);
  const days = horizonDate
    ? Math.max(1, diffDays(input.today, horizonDate))
    : input.protectDays ?? null;
  if (days === null) {
    unknowns.push('Дата следующего прихода неизвестна — назови срок, на который растянуть деньги');
  }

  // 1. Платежи: неурегулированные наступления до горизонта. Без горизонта —
  //    только те, что уже наступили: требовать денег под будущее без срока
  //    значит защищать выдуманное окно.
  const to = horizonDate
    ?? (input.protectDays !== undefined ? addDaysSafe(input.today, input.protectDays) : input.today);
  const from = horizonDate || input.protectDays !== undefined
    ? input.today
    : watchedFrom(monthStart(input.today), input.trackingStartedAt);
  /*
    Те же экземпляры, что и везде: окно до горизонта плюс неурегулированное
    прошлое. Раскладка не имеет права считать по своему календарю — иначе
    защищённый слой на главной и в предложении разойдётся.
  */
  const window = paymentInstances({
    obligations: [...input.obligations],
    reserves: [...input.reserves],
    today: input.today,
    from,
    to,
  });
  const overdue = unsettledInstances({
    obligations: input.obligations,
    reserves: input.reserves,
    today: input.today,
    ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
  });
  const byId = new Map<string, PaymentInstance>();
  for (const inst of [...overdue, ...window]) byId.set(inst.id, inst);
  const instances = [...byId.values()]
    .filter((i: PaymentInstance) => !i.settled && i.remaining > ZERO)
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));

  /*
    Платежи по картам до горизонта — те же, что вычитает «Можно потратить».
    Встают в очередь вместе с обязательствами, по сроку: деньги первым
    получает тот платёж, что наступает раньше.
  */
  const cardsNow = cardsDue(input, input.today, to).filter((e) => notCovered(e) > ZERO);
  const queue: Array<{ date: PlainDate; inst?: PaymentInstance; card?: ForecastEvent }> = [
    ...instances.map((inst) => ({ date: inst.scheduledDate, inst })),
    ...cardsNow.map((card) => ({ date: card.date, card })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  const obligations: AllocationObligationLine[] = [];
  const cards: AllocationCardLine[] = [];
  let obligationsNeeded: Money = ZERO;
  for (const item of queue) {
    const need = item.inst ? item.inst.remaining : notCovered(item.card!);
    obligationsNeeded = add(obligationsNeeded, need);
    const take = need < rest ? need : rest;
    if (take <= ZERO) continue;
    if (item.inst) {
      obligations.push({
        instanceId: item.inst.id,
        obligationId: item.inst.obligationId,
        label: item.inst.label,
        dueDate: item.inst.scheduledDate,
        amount: take,
      });
    } else {
      cards.push({
        accountId: item.card!.ref, label: item.card!.label, dueDate: item.card!.date, amount: take,
      });
    }
    rest = sub(rest, take);
  }

  // 2. Обычная жизнь до горизонта.
  let need: Money = ZERO;
  let needNeeded: Money = ZERO;
  if (!isKnown(input.needBaseline)) {
    unknowns.push('Обычные траты неизвестны — без них нельзя понять, что из остатка свободно');
  } else if (days !== null) {
    needNeeded = share(input.needBaseline.value, days / 30);
    need = needNeeded < rest ? needNeeded : rest;
    rest = sub(rest, need);
  }

  // 3. Перенос через ближайший приход: эти деньги нельзя раздавать.
  const carry = carryThroughIncome({
    today: input.today,
    incomeStreams: input.incomeStreams,
    obligations: input.obligations,
    reserves: input.reserves,
    needBaseline: input.needBaseline,
    horizon: input.horizon,
    accounts: input.accounts,
    ...(input.creditTerms ? { creditTerms: input.creditTerms } : {}),
  });
  const carryTaken = carry < rest ? carry : rest;
  rest = sub(rest, carryTaken);

  const shortfall = clampPos(sub(add(obligationsNeeded, needNeeded), clampPos(sub(liquid, reserved))));

  // 4. Цели и «Свободные траты». При нехватке не получают ничего: раздавать то, чего
  //    не хватает на платежи и еду, — самый дорогой вид оптимизма.
  const goals: AllocationGoalLine[] = [];
  const atRisk: AllocationGoalLine[] = [];
  const canFill = shortfall === ZERO && days !== null && unknowns.length === 0;

  for (const g of input.goals) {
    if (g.status !== 'active' || g.monthlyContribution <= ZERO) continue;
    if (g.contributionMode === 'residual') continue;
    if (g.startDate && isAfter(g.startDate, input.today)) continue;
    // Удерживается только невыполненная часть взноса: иначе каждый приход
    // месяца снова требовал бы полную сумму.
    const done = add(
      input.fundedThisMonth?.[g.id] ?? ZERO,
      coveredFor(input.reserves, 'goal', g.id),
    );
    const left = clampPos(sub(g.monthlyContribution, done));
    if (left <= ZERO) continue;

    if (!canFill || rest <= ZERO) {
      atRisk.push({ goalId: g.id, name: g.name, amount: left });
      continue;
    }
    const take = left < rest ? left : rest;
    goals.push({ goalId: g.id, name: g.name, amount: take });
    rest = sub(rest, take);
    if (take < left) atRisk.push({ goalId: g.id, name: g.name, amount: sub(left, take) });
  }

  // 5. Остаток — «Свободные траты», но только когда расчёт полон. Иначе он остаётся
  //    нераспределённым: называть свободным то, что не посчитано, нельзя.
  const want = canFill ? selfShare(rest, input.mode) : ZERO;
  const unallocated = sub(rest, want);

  return {
    lines: {
      obligations,
      // Поле появляется только когда есть что закрепить: так версии без карт
      // остаются такими же, как до них.
      ...(cards.length > 0 ? { cards } : {}),
      need, want, goals, carry: carryTaken, unallocated,
    },
    liquid,
    reserved,
    shortfall,
    atRisk,
    unknowns,
    inputVersion: allocationInputVersion(input),
    structureVersion: allocationStructureVersion(input),
  };
}

/**
 * Строки конфликта: что именно не помещается в деньги.
 *
 * Считается той же арифметикой, что и предложение, но показывается иначе:
 * не «не хватает 18 000», а поимённо — какой платёж, к какому сроку, сколько
 * уже обеспечено и чем обернётся нехватка именно здесь. Без этого выбор
 * человека был бы выбором вслепую.
 */
export function shortfallLines(input: AllocationPlanInput): ShortfallLine[] {
  const proposal = proposeAllocation(input);
  if (proposal.shortfall <= ZERO) return [];

  const horizonDate = confirmedDate(input.horizon);
  const days = horizonDate
    ? Math.max(1, diffDays(input.today, horizonDate))
    : input.protectDays ?? null;
  const to = horizonDate
    ?? (input.protectDays !== undefined ? addDaysSafe(input.today, input.protectDays) : input.today);

  const lines: ShortfallLine[] = [];
  for (const inst of paymentInstances({
    obligations: [...input.obligations],
    reserves: [...input.reserves],
    today: input.today,
    from: horizonDate || input.protectDays !== undefined
      ? input.today : watchedFrom(monthStart(input.today), input.trackingStartedAt),
    to,
  })) {
    if (inst.settled || inst.remaining <= ZERO) continue;
    const proposed = proposal.lines.obligations.find((o) => o.instanceId === inst.id);
    lines.push({
      kind: 'obligation',
      id: inst.id,
      label: inst.label,
      dueDate: inst.scheduledDate,
      required: inst.amount,
      secured: inst.secured,
      proposed: proposed?.amount ?? ZERO,
      consequence: consequenceOf(inst),
    });
  }

  for (const card of cardsDue(input, input.today, to)) {
    if (notCovered(card) <= ZERO) continue;
    const proposed = (proposal.lines.cards ?? []).find(
      (c) => c.accountId === card.ref && c.dueDate === card.date,
    );
    lines.push({
      kind: 'card',
      id: `card:${card.ref}:${card.date}`,
      label: card.label,
      dueDate: card.date,
      required: card.amount,
      secured: card.covered,
      proposed: proposed?.amount ?? ZERO,
      consequence: 'Проценты и потеря беспроцентного периода',
    });
  }

  if (isKnown(input.needBaseline) && days !== null) {
    const required = share(input.needBaseline.value, days / 30);
    lines.push({
      kind: 'need',
      id: 'need',
      label: 'Повседневные нужды',
      required,
      secured: ZERO,
      proposed: proposal.lines.need,
      consequence: 'Меньше денег на еду и дорогу до ближайшего прихода',
    });
  }

  return lines;
}

/**
 * Чем обернётся неоплаченный платёж.
 *
 * Названо словами, а не значком: «просрочка» и «потеря беспроцентного
 * периода» стоят разного, и выбирать между ними человек должен понимая цену.
 */
function consequenceOf(inst: PaymentInstance): string {
  if (/кредитк|карт/i.test(inst.label)) return 'Проценты и потеря беспроцентного периода';
  if (/кредит|ипотек|рассрочк|займ/i.test(inst.label)) return 'Просрочка по кредиту и штраф';
  if (/аренд|квартир|жиль/i.test(inst.label)) return 'Долг перед арендодателем';
  return 'Просрочка платежа';
}

/*
  Доля «Свободные траты» берётся из общей таблицы (`mode.ts`).

  Здесь была своя копия, и она разошлась с месячным планом: у `goals_first`
  раскладка делила остаток 30/70, а план — 25/75. Один и тот же выбор
  человека давал два разных числа на соседних экранах.
*/

const monthStart = (today: PlainDate): PlainDate =>
  `${today.slice(0, 7)}-01` as PlainDate;

const addDaysSafe = (today: PlainDate, days: number): PlainDate => {
  const base = new Date(`${today}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + Math.max(0, days));
  return base.toISOString().slice(0, 10) as PlainDate;
};

/**
 * Отпечаток данных, на которых построено предложение.
 *
 * Предложение живёт секунды: человек читает его, в это время приходит
 * зарплата или списывается платёж. Применить старое предложение к новым
 * данным — значит закрепить суммы, которых уже нет.
 */
export function allocationInputVersion(input: AllocationPlanInput): string {
  const parts = [
    input.today,
    ...input.accounts.map((a) => `a${a.id}:${a.balance}:${a.isLiquid ? 1 : 0}:${a.archived ? 1 : 0}`),
    ...input.reserves.map((r) => `r${r.id}:${r.accountId}:${r.forKind}:${r.forId}:${r.amount}`),
    ...input.obligations.map((o) => [
      `o${o.id}`, o.amount, o.isActive ? 1 : 0,
      (o.payments ?? []).map((p) => `${p.scheduledDate}=${p.paid}`).join(','),
    ].join(':')),
    // Выписка и внесённое по карте меняют защищённый слой так же, как платёж.
    ...(input.creditTerms ?? []).map((t) => [
      `c${t.accountId}`, t.statementDebt, t.reserved, t.paymentDay, t.statementDay ?? '',
    ].join(':')),
    ...input.goals.map((g) => `g${g.id}:${g.monthlyContribution}:${g.currentAmount}:${g.status}`),
    `h${input.horizon.basis}:${input.horizon.date ?? ''}`,
    `m${input.mode}`,
    `p${input.protectDays ?? ''}`,
    `t${input.trackingStartedAt ?? ''}`,
    `c${input.current?.id ?? ''}`,
  ];
  return parts.join('|');
}

/**
 * Отпечаток решений, а не денег: по нему принятая раскладка «устаревает».
 *
 * `inputVersion` защищает запись от гонки и потому ловит **всё**: дату,
 * остатки, конверты, номер текущей версии. Как ответ на вопрос «устарела ли
 * раскладка» он не годился вовсе. Сама раскладка заводит конверты и
 * становится текущей — значит, сразу после принятия отпечаток уже не
 * совпадал, и карточка «Раскладку нужно обновить» висела на главной
 * постоянно, в том числе после повторного пересчёта. Любая трата меняла
 * остаток, каждое утро менялась дата — и снова «устарела».
 *
 * Раскладка устаревает, когда меняются условия, под которые она собрана:
 * платежи и их расписание, цели, доход, обычная жизнь, горизонт, режим,
 * набор счетов. Траты сюда не входят: деньги, ушедшие мимо раскладки, ловит
 * сверка остатков («Счета разошлись с раскладкой»), и называть это
 * устаревшим планом значило бы звать пересчитывать после каждой покупки.
 */
export function allocationStructureVersion(input: AllocationPlanInput): string {
  const need = input.needBaseline.known ? String(input.needBaseline.value) : '?';
  const parts = [
    ...input.accounts
      .filter((a) => !a.archived)
      .map((a) => `a${a.id}:${a.kind}:${a.isLiquid ? 1 : 0}`),
    ...input.obligations.map((o) => [
      `o${o.id}`, o.amount, o.isActive ? 1 : 0, o.dayOfMonth, JSON.stringify(o.schedule ?? null),
    ].join(':')),
    ...input.goals.map((g) => [
      `g${g.id}`, g.monthlyContribution, g.targetAmount, g.status, g.targetDate ?? '',
    ].join(':')),
    // Ритм дохода — через `rhythmOf`: старое поле числа месяца расчётам не
    // отдаётся (apps/web/test/income-legacy-field.test.ts).
    ...input.incomeStreams.map((x) => [
      `i${x.id}`, x.amount, x.isActive ? 1 : 0, JSON.stringify(rhythmOf(x)),
    ].join(':')),
    `n${need}`,
    `h${input.horizon.basis}:${input.horizon.date ?? ''}`,
    `m${input.mode}`,
    `p${input.protectDays ?? ''}`,
    `t${input.trackingStartedAt ?? ''}`,
  ];
  return parts.join('|');
}

// ──────────────────────────────────────────── распределение остатка

/**
 * Куда человек направляет нераспределённые деньги.
 *
 * `need` и `want` увеличивают корзины: деньги остаются доступными, меняется
 * только их назначение. `goal` и `obligation` заводят конверт: деньги
 * перестают быть доступными, потому что обещаны другому.
 *
 * Отдельного «оставить доступными» в этом списке нет намеренно: всё, чего
 * человек не назначил, и так остаётся нераспределённым. Пункт, который
 * «ничего не делает», человек читает как действие и ищет в нём смысл.
 */
export type RestTargetKind = 'need' | 'want' | 'goal' | 'obligation';

export interface RestTarget {
  kind: RestTargetKind;
  /** Цель или обязательство: для корзин не нужен. */
  id?: string;
  amount: Money;
}

/** Числа до и после — то, что человек видит перед применением. */
export interface RestSnapshot {
  available: Money;
  need: Money;
  want: Money;
  unallocated: Money;
  reserved: Money;
  /** По конвертам: сколько станет у каждой цели и обязательства. */
  envelopes: { kind: ReserveTarget; id: string; amount: Money }[];
}

export interface RestPlan {
  ok: boolean;
  reason?: string;
  /** Сколько человек назначил всего. */
  assigned: Money;
  /** Сколько останется нераспределённым. */
  left: Money;
  /** Новые строки раскладки. */
  lines: AllocationLines;
  /** Прибавка к конвертам: сколько закрепить сверх уже закреплённого. */
  add: { kind: ReserveTarget; id: string; amount: Money }[];
  before: RestSnapshot;
  after: RestSnapshot;
}

/**
 * Распределить остаток принятой раскладки.
 *
 * Считается здесь, а не в экране, по той же причине, по которой здесь
 * считается всё остальное: у одного и того же вопроса не может быть двух
 * ответов. Экран показывает последствия **до** применения, и показывает он
 * ровно те числа, которые потом и запишутся.
 *
 * Главное различие, которое обязано быть видно человеку:
 *
 * · перевод в корзину меняет **состав** доступных денег, а сумму «Доступно»
 *   не трогает — деньги как были его, так и остались;
 * · перевод в цель или под платёж **уменьшает** «Доступно»: они обещаны.
 */
export function distributeRest(input: {
  allocation: AcceptedAllocation;
  /**
   * Текущие остатки строк.
   *
   * Принятая версия хранит снимок на момент согласия. После него человек
   * мог потратить часть корзины или получить новый приход, поэтому
   * распределять `allocation.lines` повторно нельзя: так траты воскресают,
   * а новые деньги исчезают из доступного остатка.
   */
  current?: Pick<AllocationLines, 'need' | 'want' | 'carry' | 'unallocated' | 'owed'>;
  /** Деньги на счетах для трат и уже закреплённое — на момент показа. */
  liquid: Money;
  reserved: Money;
  /** Сколько уже лежит в каждом конверте: `${kind}:${id}` → сумма. */
  envelopes?: Record<string, Money>;
  targets: readonly RestTarget[];
}): RestPlan {
  const { lines } = input.allocation;
  const current = input.current ?? {
    need: lines.need,
    want: lines.want,
    carry: lines.carry,
    unallocated: lines.unallocated,
  };
  const rest = current.unallocated;
  const used = input.targets.filter((t) => t.amount > ZERO);
  const assigned = add(...used.map((t) => t.amount));

  const snapshot = (
    need: Money, want: Money, unallocated: Money, reserved: Money,
    envelopes: RestSnapshot['envelopes'],
  ): RestSnapshot => ({
    available: sub(input.liquid, reserved),
    need,
    want,
    unallocated,
    reserved,
    envelopes,
  });

  const envelopeOf = (kind: ReserveTarget, id: string): Money =>
    input.envelopes?.[`${kind}:${id}`] ?? ZERO;

  const before = snapshot(
    current.need, current.want, rest, input.reserved,
    used
      .filter((t) => t.kind !== 'need' && t.kind !== 'want')
      .map((t) => ({
        kind: (t.kind === 'goal' ? 'goal' : 'obligation') as ReserveTarget,
        id: t.id ?? '',
        amount: envelopeOf(t.kind === 'goal' ? 'goal' : 'obligation', t.id ?? ''),
      })),
  );

  if (assigned > rest) {
    return {
      ok: false,
      reason: `Нераспределённых денег ${formatMoney(rest)} — меньше, чем ты назначил`,
      assigned,
      left: ZERO,
      lines,
      add: [],
      before,
      after: before,
    };
  }
  if (used.some((t) => (t.kind === 'goal' || t.kind === 'obligation') && !t.id)) {
    return {
      ok: false, reason: 'Не выбрано, какой цели или платежу', assigned,
      left: sub(rest, assigned), lines, add: [], before, after: before,
    };
  }

  const toNeed = add(...used.filter((t) => t.kind === 'need').map((t) => t.amount));
  const toWant = add(...used.filter((t) => t.kind === 'want').map((t) => t.amount));
  const bound = used.filter((t) => t.kind === 'goal' || t.kind === 'obligation');
  const addEnvelopes = bound.map((t) => ({
    kind: (t.kind === 'goal' ? 'goal' : 'obligation') as ReserveTarget,
    id: t.id ?? '',
    amount: t.amount,
  }));
  const toEnvelopes = add(...addEnvelopes.map((e) => e.amount));

  const nextLines: AllocationLines = {
    ...lines,
    need: add(current.need, toNeed),
    want: add(current.want, toWant),
    carry: current.carry,
    /*
      Конверт снимает деньги с раскладки целиком: они больше не доступны, а
      значит, их нет ни в корзинах, ни в нераспределённом.
    */
    unallocated: sub(rest, assigned),
    goals: mergeGoalLines(lines.goals, bound),
  };
  // Долг перед картой — тот, что на сейчас, а не из прежней строки.
  if (input.current) {
    if (input.current.owed && input.current.owed.length > 0) nextLines.owed = input.current.owed;
    else delete nextLines.owed;
  }

  const reservedAfter = add(input.reserved, toEnvelopes);
  const after = snapshot(
    nextLines.need, nextLines.want, nextLines.unallocated, reservedAfter,
    addEnvelopes.map((e) => ({ ...e, amount: add(envelopeOf(e.kind, e.id), e.amount) })),
  );

  return {
    ok: true,
    assigned,
    left: nextLines.unallocated,
    lines: nextLines,
    add: addEnvelopes,
    before,
    after,
  };
}

/** Строки целей в раскладке: назначенное сверх прежнего, а не вместо. */
function mergeGoalLines(
  current: AllocationGoalLine[], targets: readonly RestTarget[],
): AllocationGoalLine[] {
  const out = current.map((g) => ({ ...g }));
  for (const t of targets) {
    if (t.kind !== 'goal' || !t.id) continue;
    const found = out.find((g) => g.goalId === t.id);
    if (found) found.amount = add(found.amount, t.amount);
    else out.push({ goalId: t.id, name: '', amount: t.amount });
  }
  return out;
}

// ──────────────────────────────────────────────────── что стало с корзинами

/**
 * Операция глазами раскладки.
 *
 * Привязка к версии, а не к дате: пересогласование посреди дня и запись
 * задним числом иначе попадут не в ту корзину. Версия ставится в момент
 * записи — то есть та, в которой человек находился, когда рассказал о
 * событии.
 */
export interface AllocationEntry {
  type: 'expense' | 'income' | 'transfer' | 'adjustment';
  amount: Money;
  part?: 'need' | 'want';
  /** Версия раскладки, действовавшая в момент записи. */
  allocationId?: string;
  /**
   * Изменила ли операция остаток счёта.
   *
   * `false` — «этот расход уже сидит в названном остатке». Такая операция
   * корзину не уменьшает: деньги ушли до того, как раскладка была принята.
   */
  balanceApplied?: boolean;
  /** Приход: разовый и регулярный одинаково попадают в «не распределено». */
  incomeKind?: 'regular' | 'oneoff' | 'refund';
  /**
   * Что исполняет операция.
   *
   * Платёж по обязательству и взнос в цель тратятся из **связанных** денег, а
   * не из корзин: конверт под них уже снят, счёт уменьшился на ту же сумму, и
   * «Можно потратить» от этого не меняется. Вычесть их ещё и из «Повседневные нужды»
   * значило бы списать одни и те же деньги дважды.
   */
  settles?: 'obligation' | 'goal';
  /**
   * Счёт операции; у перевода — откуда. Без счёта операция читается как
   * прежде — по тратимым деньгам.
   */
  accountId?: string;
  /** Перевод: куда. Без него перевод — между своими тратимыми счетами. */
  toAccountId?: string;
  /**
   * Сколько платёж взял из отложенного под него (конверт платежа, деньги под
   * кредитку). Остальное ушло из свободных денег. Без поля — старая запись:
   * отложенным считается то, что раскладка держала под это наступление.
   */
  reserveUsed?: Money;
  /** Наступление платежа (`obligationId:дата`) — для старых записей без `reserveUsed`. */
  instanceId?: string;
  /**
   * Движение уже учтено отдельно (выплата себе из буфера, взнос в цель):
   * по переводу корзины не пересчитываются.
   */
  accounted?: boolean;
}

export interface AllocationView {
  /**
   * Покупки по кредитке, ещё не погашенные с тратимых счетов: деньги лежат
   * на карте для трат, но обещаны кредитке.
   */
  owed: Money;
  /** То же по картам — переносится в следующую версию при «Распределить». */
  owedBy: { accountId: string; amount: Money }[];
  /**
   * Свободных денег не хватило: платежи и переводы взяли больше, чем было не
   * распределено. На столько же меньше «Можно тратить».
   */
  short: Money;
  /** Остаток «Повседневные нужды». */
  needLeft: Money;
  /** Остаток «Свободные траты». */
  wantLeft: Money;
  carry: Money;
  /** Не распределено: сюда падают новые приходы. */
  unallocated: Money;
  /** Связанные деньги — по резервам, а не по строкам раскладки. */
  reserved: Money;
  liquid: Money;
  /** Сколько можно потратить прямо сейчас. */
  spendable: Money;
  /**
   * Расхождение между деньгами и корзинами.
   *
   * Ноль — картина сходится. Плюс — денег больше, чем разложено («появилось
   * +5 000»), минус — меньше («не хватает 3 000»). Приложение не исправляет
   * его молча: человек видит расхождение и принимает новую версию.
   */
  drift: Money;
  balanced: boolean;
  /**
   * Перерасход корзин: сколько потрачено сверх «Повседневных нужд» и
   * «Свободных трат». Корзина не уходит ниже нуля в `spendable`, и без этого
   * числа перерасход прятался в `drift` — на экране он читался как «деньги
   * двигались мимо приложения», хотя трату записали в приложении.
   */
  overspent: Money;
  /**
   * Расхождение без перерасхода: деньги на счетах изменились мимо записей —
   * правка остатка, снятие конверта, операция без версии раскладки.
   * `drift = unexplained − overspent`.
   */
  unexplained: Money;
}

/**
 * Остатки корзин на сейчас.
 *
 * Трата `need` уменьшает только «Повседневные нужды», трата `want` — только «Свободные траты»,
 * приход увеличивает «не распределено». Перевод между своими счетами не
 * меняет ничего: капитал не изменился.
 */
export function allocationView(input: {
  allocation: AcceptedAllocation;
  accounts: readonly Account[];
  reserves: readonly Reserve[];
  entries: readonly AllocationEntry[];
  /**
   * Выплаты себе из буфера стабильного бюджета.
   *
   * Доходом они не являются — деньги не появились, — но «не распределено»
   * растёт ровно на их сумму, и картина от этого сходится сама: если буфер
   * лежал на тратимом счёте, на столько же уменьшился резерв; если на
   * отдельном — на столько же выросли ликвидные деньги. Не учесть их значило
   * бы каждый раз показывать расхождение там, где всё в порядке.
   */
  selfPayouts?: readonly { amount: Money; allocationId?: string }[];
  /**
   * Прочие изменения назначения стабильного запаса: пополнение,
   * внеплановое изъятие, освобождение и сверка. Плановые выплаты идут выше,
   * чтобы старые факты выплат продолжали читаться без миграции.
   */
  bufferAdjustments?: readonly { delta: Money; allocationId?: string }[];
}): AllocationView {
  const { lines } = input.allocation;
  const mine = input.entries.filter(
    (e) => e.allocationId === input.allocation.id && e.balanceApplied !== false,
  );

  const spent = (part: 'need' | 'want'): Money => add(
    ...mine
      .filter((e) => e.type === 'expense' && !e.settles)
      .filter((e) => (e.part ?? 'need') === part)
      .map((e) => e.amount),
  );
  // Возврат — не доход: он возвращает деньги в свою корзину.
  const refunded = (part: 'need' | 'want'): Money => add(
    ...mine
      .filter((e) => e.type === 'income' && e.incomeKind === 'refund' && (e.part ?? 'need') === part)
      .map((e) => e.amount),
  );
  /*
    Тратимые ли деньги двигала операция.

    Покупка по кредитке тратит корзину, а деньги на тратимых счетах не
    трогает: они обещаны карте до погашения (`owed`). Погашение с карты для
    трат — сначала из отложенного под карту, потом в счёт этих покупок,
    остальное — из свободных денег. Платёж без отложенных под него денег —
    тоже из свободных. Перевод на накопительный счёт — из свободных, обратно —
    в свободные. Приход на кредитку или накопительный счёт денег для трат не
    добавляет. Раньше всё это читалось как «деньги двигались мимо
    приложения», хотя каждая операция записана.

    Старые записи платежей и погашений — без `reserveUsed`: сколько взято из
    конверта, неизвестно, и они читаются как прежде. Догадка по строкам
    раскладки ошибалась: конверт прошлой версии в строках не виден.
  */
  const accountOf = (id: string | undefined) => (id === undefined ? undefined : input.accounts.find((a) => a.id === id));
  // Тратимый — как в `liquidOf`: скрытый счёт и кредитка тратимыми не считаются.
  const liquidAcc = (id: string | undefined): boolean => {
    if (id === undefined) return true;
    const a = accountOf(id);
    return !a || (a.isLiquid && !a.archived && a.kind !== 'credit_card');
  };
  const cardAcc = (id: string | undefined): boolean => accountOf(id)?.kind === 'credit_card';
  const owedBy = new Map<string, Money>();
  for (const o of lines.owed ?? []) owedBy.set(o.accountId, add(owedBy.get(o.accountId) ?? ZERO, o.amount));
  const owe = (id: string, m: Money) => owedBy.set(id, add(owedBy.get(id) ?? ZERO, m));
  let fromFree = ZERO;
  let toFree = ZERO;
  for (const e of mine) {
    if (e.type === 'expense' && e.settles === 'obligation') {
      if (e.reserveUsed === undefined) continue;
      const covered = minM(e.reserveUsed, e.amount);
      const uncovered = sub(e.amount, covered);
      if (liquidAcc(e.accountId)) fromFree = add(fromFree, uncovered);
      else if (cardAcc(e.accountId)) { owe(e.accountId!, e.amount); fromFree = add(fromFree, uncovered); }
      // С накопительного: тратимые не тронуты, а снятый конверт освободил деньги.
      else toFree = add(toFree, covered);
    } else if (e.type === 'expense' && !e.settles && cardAcc(e.accountId)) {
      owe(e.accountId!, e.amount);
    } else if (e.type === 'income' && e.incomeKind === 'refund' && cardAcc(e.accountId)) {
      owe(e.accountId!, neg(e.amount));
    }
  }
  // Погашения — после покупок: в счёт покупок, остальное — свободными.
  for (const e of mine) {
    if (e.type !== 'transfer' || e.accounted || e.toAccountId === undefined) continue;
    const fromL = liquidAcc(e.accountId);
    const toL = liquidAcc(e.toAccountId);
    if (fromL && !toL && cardAcc(e.toAccountId)) {
      // Старое погашение: покупки этой раскладки им оплачены, остальное — как прежде.
      if (e.reserveUsed === undefined) {
        owe(e.toAccountId, neg(minM(e.amount, clampPos(owedBy.get(e.toAccountId) ?? ZERO))));
        continue;
      }
      const pay = clampPos(sub(e.amount, e.reserveUsed));
      const covered = minM(pay, clampPos(owedBy.get(e.toAccountId) ?? ZERO));
      owe(e.toAccountId, neg(covered));
      fromFree = add(fromFree, sub(pay, covered));
    } else if (fromL && !toL) {
      fromFree = add(fromFree, e.amount);
    } else if (!fromL && toL) {
      toFree = add(toFree, e.amount);
    }
  }
  const owed = add(...[...owedBy.values()]);

  const arrived = add(
    ...mine
      .filter((e) => e.type === 'income' && e.incomeKind !== 'refund' && liquidAcc(e.accountId))
      .map((e) => e.amount),
    toFree,
  );

  const needLeft = sub(add(lines.need, refunded('need')), spent('need'));
  const wantLeft = sub(add(lines.want, refunded('want')), spent('want'));
  const released = add(
    ...(input.selfPayouts ?? [])
      .filter((p) => p.allocationId === input.allocation.id)
      .map((p) => p.amount),
  );
  const adjusted = add(
    ...(input.bufferAdjustments ?? [])
      .filter((m) => m.allocationId === input.allocation.id)
      .map((m) => m.delta),
  );
  /*
    Корректировка остатка тратимого счёта — в «не распределено», как сверка
    запаса: деньги оказались другими, чем записано, и разложены они ещё не
    были. Иначе каждая корректировка висела бы расхождением «на счетах
    больше, чем в корзинах», хотя человек сам его и исправил.
  */
  const corrected = add(
    ZERO,
    ...mine.filter((e) => e.type === 'adjustment' && liquidAcc(e.accountId)).map((e) => e.amount),
  );
  const unallocated = sub(add(lines.unallocated, arrived, released, adjusted, corrected), fromFree);
  /*
    Свободных не хватило: платежи и переводы взяли больше, чем было не
    распределено, — значит, взяли из корзин. «Можно тратить» без этого
    обещало бы деньги, которых на счетах уже нет.
  */
  const short = clampPos(neg(unallocated));
  const reserved = reservedOf(input.accounts, input.reserves);
  const liquid = liquidOf(input.accounts);

  const buckets = add(reserved, clampPos(needLeft), clampPos(wantLeft), lines.carry, unallocated, owed);
  const drift = sub(liquid, buckets);
  const overspent = add(clampPos(sub(ZERO, needLeft)), clampPos(sub(ZERO, wantLeft)));

  return {
    owed,
    needLeft,
    wantLeft,
    carry: lines.carry,
    unallocated,
    reserved,
    liquid,
    spendable: clampPos(sub(add(clampPos(needLeft), clampPos(wantLeft)), short)),
    short,
    owedBy: [...owedBy.entries()].filter(([, m]) => m !== ZERO).map(([accountId, amount]) => ({ accountId, amount })),
    drift,
    balanced: drift === ZERO && short === ZERO,
    overspent,
    unexplained: add(drift, overspent),
  };
}

/** Действующая версия: последняя принятая. */
export const activeAllocation = (
  list: readonly AcceptedAllocation[],
): AcceptedAllocation | null => {
  const sorted = [...list].sort((a, b) => a.acceptedAt.localeCompare(b.acceptedAt));
  return sorted.length > 0 ? sorted[sorted.length - 1]! : null;
};

/** Начало окна, но не раньше начала учёта: прежние платежи шли мимо приложения. */
function watchedFrom(from: PlainDate, trackingStartedAt: PlainDate | undefined): PlainDate {
  return trackingStartedAt && isAfter(trackingStartedAt, from) ? trackingStartedAt : from;
}
